/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ApiResponse } from '../../types/apiTypes';
import {
  UserActivity,
  UserActivityType,
  LogUserActivityPayload,
} from '../../types/userActivityTypes';

export interface UserActivityState {
  activities: ApiResponse<UserActivity>;
  selectedActivity: UserActivity | null;
  activityTypes: ApiResponse<UserActivityType>;
  fetchUserActivitiesLoading: boolean;
  fetchUserActivityTypesLoading: boolean;
  logUserActivityLoading: boolean;
  error: string | null;
}

const initialState: UserActivityState = {
  activities: { data: [], meta: null, totalCount: 0, filteredCount: 0 },
  selectedActivity: null,
  activityTypes: { data: [], meta: null, totalCount: 0, filteredCount: 0 },
  fetchUserActivitiesLoading: false,
  fetchUserActivityTypesLoading: false,
  logUserActivityLoading: false,
  error: null,
};

type UserActivityLoadingKey =
  | 'fetchUserActivitiesLoading'
  | 'fetchUserActivityTypesLoading'
  | 'logUserActivityLoading';

const userActivitySlice = createSlice({
  name: 'userActivity',
  initialState,
  reducers: {
    fetchUserActivities(
      state,
      _action: PayloadAction<{
        orgId: number;
        startDate?: string;
        endDate?: string;
      }>,
    ) {
      state.error = null;
    },
    fetchUserActivityTypes(state, _action: PayloadAction<{ orgId: number }>) {
      state.error = null;
    },
    logUserActivity(
      state,
      _action: PayloadAction<{
        orgId: number;
        userActivity: LogUserActivityPayload;
      }>,
    ) {
      state.error = null;
    },

    setUserActivities(state, action: PayloadAction<ApiResponse<UserActivity>>) {
      state.activities = action.payload;
    },
    setUserActivityTypes(
      state,
      action: PayloadAction<ApiResponse<UserActivityType>>,
    ) {
      state.activityTypes = action.payload;
    },
    setSelectedActivity(state, action: PayloadAction<UserActivity>) {
      state.selectedActivity = action.payload;
    },

    setLoading(
      state,
      action: PayloadAction<{ key: UserActivityLoadingKey; value: boolean }>,
    ) {
      const { key, value } = action.payload;
      state[key] = value;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    clearUserActivities(state) {
      state.activities = initialState.activities;
    },
    clearActivityTypes(state) {
      state.activityTypes = initialState.activityTypes;
    },
    clearUserActivityState() {
      return initialState;
    },
  },
});

export const {
  fetchUserActivities,
  fetchUserActivityTypes,
  logUserActivity,
  setUserActivities,
  setUserActivityTypes,
  setSelectedActivity,
  setLoading,
  setError,
  clearUserActivities,
  clearActivityTypes,
  clearUserActivityState,
} = userActivitySlice.actions;

export const userActivityActions = userActivitySlice.actions;
export default userActivitySlice.reducer;
